import { ReactNode } from 'react';
import { Button, Typography, theme } from 'antd';
import { InboxOutlined, ExclamationCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { controlHeightTokens } from '@/styles/tokens';
import { useElevation } from '@/styles/useElevation';

const { Title, Text } = Typography;

/**
 * '-' empty-cell rendering (S0.3 triad, 4th member).
 * Use as the default fallback in column render functions:
 *   render: (value) => renderDash(value)
 */
// eslint-disable-next-line react-refresh/only-export-components
export const renderDash = (value?: ReactNode): ReactNode =>
  value === undefined || value === null || value === '' ? '-' : value;

export interface ListEmptyStateProps {
  /** Localized headline (e.g. "No Vendors Yet" / "No search result") */
  title: string;
  /** Optional localized secondary line */
  description?: string;
  /** Optional CTA label (create CTA on first-use, clear-filters on zero-results) */
  actionLabel?: string;
  /** CTA handler — the CTA renders only when both label and handler are set */
  onAction?: () => void;
  /** CTA button type: 'primary' for create CTA, 'default' for clear-filters */
  actionType?: 'primary' | 'default';
  /** CTA icon (e.g. <PlusOutlined /> for create) */
  actionIcon?: ReactNode;
  /** Override the headline icon (defaults to <InboxOutlined />) */
  icon?: ReactNode;
}

/**
 * Standard list empty state (S0.3 triad).
 * Covers BOTH first-use empty (+ create CTA) and filter-zero-results
 * (+ clear-filters) — the page picks title/CTA from `useListPage().listState`.
 * Render inside the Table `locale.emptyText` slot.
 */
export const ListEmptyState = ({
  title,
  description,
  actionLabel,
  onAction,
  actionType = 'primary',
  actionIcon,
  icon,
}: ListEmptyStateProps) => {
  const { token } = theme.useToken();
  const hasAction = Boolean(actionLabel && onAction);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: `${token.paddingXL * 2}px ${token.paddingXL}px`,
        textAlign: 'center',
      }}
    >
      <span
        style={{
          fontSize: token.sizeXXL * 2,
          color: token.colorTextDisabled,
          marginBottom: token.marginLG,
        }}
      >
        {icon ?? <InboxOutlined />}
      </span>
      <Title
        level={3}
        style={{
          marginBottom: description || hasAction ? token.marginLG : 0,
          color: token.colorText,
        }}
      >
        {title}
      </Title>
      {description && (
        <Text
          style={{
            color: token.colorTextSecondary,
            marginBottom: hasAction ? token.marginLG : 0,
          }}
        >
          {description}
        </Text>
      )}
      {hasAction && (
        <Button
          type={actionType}
          icon={actionIcon}
          size="large"
          style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
          onClick={onAction}
        >
          {actionLabel}
        </Button>
      )}
    </div>
  );
};

export interface ListErrorStateProps {
  /** Localized error headline (e.g. entity fetchFailed message) */
  title: string;
  /** Optional localized secondary line */
  description?: string;
  /** Localized retry button label */
  retryLabel: string;
  /** Retry handler — re-runs the fetch */
  onRetry: () => void;
}

/**
 * Standard list error state (S0.3 triad): error + retry.
 * TableContainer renders this in place of its children when `errorState.active`.
 */
export const ListErrorState = ({
  title,
  description,
  retryLabel,
  onRetry,
}: ListErrorStateProps) => {
  const { token } = theme.useToken();

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: `${token.paddingXL * 2}px ${token.paddingXL}px`,
        textAlign: 'center',
      }}
    >
      <span
        style={{
          fontSize: token.sizeXXL * 2,
          color: token.colorError,
          marginBottom: token.marginLG,
        }}
      >
        <ExclamationCircleOutlined />
      </span>
      <Title level={3} style={{ marginBottom: token.marginLG, color: token.colorText }}>
        {title}
      </Title>
      {description && (
        <Text style={{ color: token.colorTextSecondary, marginBottom: token.marginLG }}>
          {description}
        </Text>
      )}
      <Button
        type="primary"
        icon={<ReloadOutlined />}
        size="large"
        style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
        onClick={onRetry}
      >
        {retryLabel}
      </Button>
    </div>
  );
};

export interface TableContainerErrorState {
  /** When true, the standardized error+retry panel replaces the table */
  active: boolean;
  /** Localized error headline */
  title: string;
  /** Optional localized secondary line */
  description?: string;
  /** Localized retry button label */
  retryLabel: string;
  /** Retry handler */
  onRetry: () => void;
}

interface TableContainerProps {
  children: ReactNode;
  className?: string;
  /**
   * S0.3 triad: pass `useListPage().listState === 'error'` as `active` and the
   * container swaps the table for the standard error+retry state.
   */
  errorState?: TableContainerErrorState;
}

/**
 * Wrapper for tables with consistent production-enhanced styling.
 * Applies the theme-aware shadow-border ("de-border") elevation via
 * `useElevation().card` — a resting grouping-card surface: soft layered shadow +
 * decorative `ringSubtle` edge + `card` radius, replacing the old hard-coded
 * shadow. Overflow stays `visible` for the ribbon overlay pattern (see global.css).
 * Bakes in the S0.3 error state: when `errorState.active`, renders the
 * standardized error+retry panel instead of the table.
 * @param children - Table component
 * @param className - Additional CSS classes
 * @param errorState - Optional standardized error+retry state
 */
const TableContainer = ({ children, className = '', errorState }: TableContainerProps) => {
  const { token } = theme.useToken();
  const elevation = useElevation();

  return (
    <div
      className={`production-enhanced ${className}`}
      style={{
        backgroundColor: token.colorBgContainer,
        ...elevation.card,
        overflow: 'visible',
      }}
    >
      {errorState?.active ? (
        <ListErrorState
          title={errorState.title}
          description={errorState.description}
          retryLabel={errorState.retryLabel}
          onRetry={errorState.onRetry}
        />
      ) : (
        children
      )}
    </div>
  );
};

export default TableContainer;
